import React, { useState, useEffect, useContext } from "react";
import DatePicker from "react-datepicker";
import Preloader from "../shared/PreLoaders";
import { Authorization } from "../Context/Authorization";
import axios from "axios";
import ClosingBalance from "../components/CashBook/ClosingBalance";
import { AuthContext } from "../Context/AuthContext";
import CircleModal from "../components/modals/CircleModel";
import DivisionModal from "../components/modals/DivisionModel";
import { toast } from "react-toastify";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label,
} from "recharts";
import { useNavigate } from "react-router-dom";
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

const getCachedData = (key) => {
  const cached = localStorage.getItem(key);
  if (!cached) return null;

  const { data, timestamp } = JSON.parse(cached);
  if (Date.now() - timestamp < CACHE_DURATION) {
    return data;
  }

  localStorage.removeItem(key); // remove expired cache
  return null;
};

const setCachedData = (key, data) => {
  localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
};

const Dashboard = () => {
  const [startDate, setStartDate] = useState(new Date());
  const [vfcs, setVfcs] = useState(0);
  const [members, setMembers] = useState(0);
  const [totalLoans, setTotalLoans] = useState(0);
  const [totalLoanCollections, setTotalLoanCollections] = useState(0);
  const [totalShgs, setTotalShgs] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalNoOfLoans, setTotalNoOfLoans] = useState("");
  const [chartData, setChartData] = useState([]);
  const [openPopup, setOpenPopup] = useState(false);
  const { role } = useContext(AuthContext);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [circleModalOpen, setCircleModalOpen] = useState(false);
  const [divisionModalOpen, setDivisionModalOpen] = useState(false);
  const [divisionData, setDivisionData] = useState([]);
  const [vfcData, setVfcData] = useState([]);
  const navigate = useNavigate();
  const [totalShgMembers, setTotalShgMembers] = useState(0);
  const Api = process.env.REACT_APP_API;
  useEffect(() => {
    fetchDashboardData();
    fetchData();
  }, []);

  const fetchDashboardData = async () => {
    const cached = getCachedData("dashboardData");
    if (cached) {
      setVfcs(cached.totalVfcs);
      setMembers(cached.totalMembers);
      setTotalLoans(cached.totalLoans);
      setTotalLoanCollections(cached.totalLoanCollections);
      setTotalShgs(cached.totalShgs);
      setTotalNoOfLoans(cached.totalNoOfLoans);
      setTotalShgMembers(cached.totalShgMembers);
      setLoading(false);
      return;
    }

    try {
      const config = Authorization();
      const response = await fetch(`${Api}/api/all/dashboard-data`, config);

      if (!response.ok) throw new Error("Failed to fetch dashboard data");

      const data = await response.json();

      setCachedData("dashboardData", data); // ✅ cache it

      setVfcs(data.totalVfcs || 0);
      setMembers(data.totalMembers || 0);
      setTotalLoans(data.totalLoans || 0);
      setTotalLoanCollections(data.totalLoanCollections || 0);
      setTotalShgs(data.totalShgs || 0);
      setTotalNoOfLoans(data.totalNoOfLoans || 0);
      setTotalShgMembers(data.totalShgMembers || 0);
    } catch (error) {
      setError("Error fetching dashboard data");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    const cached = getCachedData("circleWiseData");
    if (cached) {
      setChartData(cached);
      return;
    }

    const config = Authorization();
    try {
      const response = await axios.get(
        `${Api}/api/all/circle-wise-data`,
        config
      );

      const formattedData = response.data
        .map((item) => ({
          ...item,
          totalVfcs: Number(item.totalVfcs) || 0,
          totalMembers: Number(item.totalMembers) || 0,
          totalLoanAmount: item.totalLoanAmount,
          totalRepaymentAmount: item.totalRepaymentAmount,
        }))
        .sort((a, b) => a.circleName.localeCompare(b.circleName));

      setChartData(formattedData);
      setCachedData("circleWiseData", formattedData); // ✅ cache it
    } catch (error) {
      console.error("Error fetching circle-wise data:", error);
    }
  };

  const formatAmount = (amount) => {
    if (amount >= 10000000) {
      return (amount / 10000000).toFixed(2) + " Cr"; // Crores
    } else if (amount >= 100000) {
      return (amount / 100000).toFixed(2) + " L"; // Lakhs
    } else if (amount >= 1000) {
      return (amount / 1000).toFixed(2) + " K"; // Thousands
    }
    return amount; // Return as is if less than 1000
  };

  const handleDateChange = (date) => {
    setStartDate(date);
  };

  const handleBarClick = (data) => {
    const circleName = data.activeLabel || data.payload?.circleName;
    if (!circleName) return;

    setSelectedCircle(circleName);
    fetchDivisionData(circleName); // Fetch API call here
    setCircleModalOpen(true);
  };

  const fetchDivisionData = async (circleName) => {
    try {
      const config = {
        ...Authorization(),
        headers: {
          ...Authorization().headers,
          "Content-Type": "application/json",
        },
      };

      const response = await axios.post(
        `${Api}/api/all/division-wise`,
        { circleName },
        config
      );
      setDivisionData(response.data || []);
    } catch (error) {
      console.error("Failed to fetch division data:", error);
    }
  };

  const handleDivisionClick = async (divisionName) => {
    setSelectedDivision(divisionName);

    try {
      const config = {
        ...Authorization(),
        headers: {
          ...Authorization().headers,
          "Content-Type": "application/json",
        },
      };

      const response = await axios.post(
        `${Api}/api/all/vfc-wise`,
        {
          circleName: selectedCircle,
          divisionName: divisionName,
        },
        config
      );

      setVfcData(response.data || []);
      setDivisionModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch VFC data:", error);
    }
  };

  const handleVfcCardClick = () => {
    if (["Admin", "Super Admin", "CCF", "DFO", "FRO"].includes(role)) {
      navigate("/drill/vfc-in-details");
    } else {
      toast.error("Access Denied Please Contact Responsible User");
    }
  };

  const handleVfcMemberCardClick = () => {
    if (["Admin", "Super Admin", "CCF", "DFO", "FRO"].includes(role)) {
      navigate("/drill/circle/member");
    } else {
      toast.error("Access Denied Please Contact Responsible User");
    }
  };
  const handleSHGCardClick = () => {
    if (["Admin", "Super Admin", "CCF", "DFO", "FRO"].includes(role)) {
      navigate("/drill/circle/SHG");
    } else {
      toast.error("Access Denied Please Contact Responsible User");
    }
  };

  const handleSHGMemberCardClick = () => {
    if (["Admin", "Super Admin", "CCF", "DFO", "FRO"].includes(role)) {
      navigate("/drill/SHG/member");
    } else {
      toast.error("Access Denied Please Contact Responsible User");
    }
  };

  const handleloanCardClick = () => {
    if (["Admin", "Super Admin", "CCF", "DFO", "FRO"].includes(role)) {
      navigate("/drill/loans");
    } else {
      toast.error("Access Denied Please Contact Responsible User");
    }
  };

  const totalVfcMembers = members - totalShgMembers;

  return (
    <div>
      <Preloader loading={loading} />
      <div className="page-header">
        <h3 className="page-title">
          <span className="page-title-icon bg-gradient-primary text-white mr-2">
            <i className="mdi mdi-home"></i>
          </span>
          Dashboard
        </h3>
        <div style={{ minWidth: 200 }}>
          <DatePicker
            selected={startDate}
            onChange={handleDateChange}
            dateFormat="dd-MMM-yyyy"
            className="form-control"
          />
        </div>
      </div>

      <div className="row">
        {role !== "Super Admin" && role !== "DFO" && role !== "CCF" && (
          <div className="col-md-6 stretch-card grid-margin">
            <div
              className="card bg-gradient-OliveDrizzle card-img-holder text-white"
              style={{ cursor: "pointer" }}
              onClick={() => setOpenPopup(true)}
            >
              <div className="card-body">
                <img
                  src={require("../../assets/images/dashboard/circle.png")}
                  className="card-img-absolute"
                  alt="circle"
                />
                <h4 className="font-weight-normal mb-3">Opening Balance</h4>
                <h1 className="mb-5">View</h1>
              </div>
            </div>
          </div>
        )}

        <div
          className={`${
            role !== "Super Admin" ? "col-md-6" : "col-md-3"
          } stretch-card grid-margin`}
        >
          <div
            className="card bg-gradient-RedDelight card-img-holder text-white"
            onClick={handleVfcCardClick}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body">
              <img
                src={require("../../assets/images/dashboard/circle.png")}
                className="card-img-absolute"
                alt="circle"
              />
              <h4 className="font-weight-normal mb-3">VFC's</h4>
              <h1 className="mb-5">{vfcs.toLocaleString()}</h1>
              {!["Super Admin", "DFO", "CCF", "FRO"].includes(role) && (
                <span
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    zIndex: 2,
                    position: "relative",
                    display: "inline-block",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/vfc/add");
                  }}
                >
                  + Add New VFC
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          className={`${
            role !== "Super Admin" ? "col-md-4" : "col-md-3"
          } stretch-card grid-margin`}
        >
          <div
            className="card bg-gradient-RainforestMist card-img-holder text-white"
            onClick={handleVfcMemberCardClick}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body">
              <img
                src={require("../../assets/images/dashboard/circle.png")}
                className="card-img-absolute"
                alt="circle"
              />
              <h4 className="font-weight-normal mb-3">VFC-MEMBERS</h4>
              <h1 className="mb-5">{totalVfcMembers.toLocaleString()}</h1>
              {!["Super Admin", "DFO", "CCF", "FRO"].includes(role) && (
                <span
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    zIndex: 2,
                    position: "relative",
                    display: "inline-block",
                  }}
                  onClick={(e) => {
                    navigate("/member/add");
                  }}
                >
                  + Add New Member
                </span>
              )}
            </div>
          </div>
        </div>
        <div
          className={`${
            role !== "Super Admin" ? "col-md-4" : "col-md-3"
          } stretch-card grid-margin`}
        >
          <div
            className="card bg-gradient-success card-img-holder text-white"
            onClick={handleSHGCardClick}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body">
              <img
                src={require("../../assets/images/dashboard/circle.png")}
                className="card-img-absolute"
                alt="circle"
              />
              <h4 className="font-weight-normal mb-3">SHG's</h4>
              <h1 className="mb-5">{totalShgs.toLocaleString()}</h1>
              {!["Super Admin", "DFO", "CCF", "FRO"].includes(role) && (
                <span
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    zIndex: 2,
                    position: "relative",
                    display: "inline-block",
                  }}
                  onClick={(e) => {
                    navigate("/SHG/add");
                  }}
                >
                  + Add New SHG
                </span>
              )}
            </div>
          </div>
        </div>
        <div
          className={`${
            role !== "Super Admin" ? "col-md-4" : "col-md-3"
          } stretch-card grid-margin`}
        >
          <div
            className="card bg-gradient-dark card-img-holder text-white"
            onClick={handleSHGMemberCardClick}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body">
              <img
                src={require("../../assets/images/dashboard/circle.png")}
                className="card-img-absolute"
                alt="circle"
              />
              <h4 className="font-weight-normal mb-3">SHG-Members</h4>
              <h1 className="mb-5">{totalShgMembers.toLocaleString()}</h1>
              {!["Super Admin", "DFO", "CCF", "FRO"].includes(role) && (
                <span
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    zIndex: 2,
                    position: "relative",
                    display: "inline-block",
                  }}
                  onClick={(e) => {
                    navigate("/SHG/join");
                  }}
                >
                  + Add SHG Member
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4 stretch-card grid-margin">
          <div
            className="card bg-gradient-info card-img-holder text-white"
            onClick={handleloanCardClick}
            style={{ cursor: "pointer" }}
          >
            <div className="card-body">
              <img
                src={require("../../assets/images/dashboard/circle.png")}
                className="card-img-absolute"
                alt="circle"
              />
              <h4 className="font-weight-normal mb-3">
                Number Of Loans - VFC's
              </h4>
              <h1 className="mb-5">{totalNoOfLoans.toLocaleString()}</h1>
              {!["Super Admin", "DFO", "CCF", "FRO"].includes(role) && (
                <span
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    zIndex: 2,
                    position: "relative",
                    display: "inline-block",
                  }}
                  onClick={(e) => {
                    navigate("/loan/new");
                  }}
                >
                  + Add New Loans
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-4 stretch-card grid-margin">
          <div className="card bg-gradient-warning card-img-holder text-white">
            <div className="card-body">
              <img
                src={require("../../assets/images/dashboard/circle.png")}
                className="card-img-absolute"
                alt="circle"
              />
              <h4 className="font-weight-normal mb-3">Loan Amount - VFC's</h4>
              <h1 className="mb-5">₹{formatAmount(totalLoans)}</h1>
            </div>
          </div>
        </div>
        <div className="col-md-4 stretch-card grid-margin">
          <div className="card bg-gradient-primary card-img-holder text-white">
            <div className="card-body">
              <img
                src={require("../../assets/images/dashboard/circle.png")}
                className="card-img-absolute"
                alt="circle"
              />
              <h4 className="font-weight-normal mb-3">
                Recovered Amount - VFC's
              </h4>
              <h1 className="mb-5">₹{formatAmount(totalLoanCollections)}</h1>
              {!["Super Admin", "DFO", "CCF", "FRO"].includes(role) && (
                <span
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    zIndex: 2,
                    position: "relative",
                    display: "inline-block",
                  }}
                  onClick={(e) => {
                    navigate("/loan/repayment");
                  }}
                >
                  + Add New Loan Repayments
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 stretch-card grid-margin">
          <div className="d-flex justify-content-center mb-2">
            <strong>VFC's in Circles</strong>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis
                  dataKey="circleName"
                  angle={-90}
                  textAnchor="end"
                  interval={0}
                  height={80}
                  tick={{ fontSize: 14 }}
                />
                <YAxis
                  tickFormatter={(value) => formatAmount(value)}
                  domain={[0, "auto"]}
                >
                  <Label
                    value="VFC's in Numbers"
                    angle={-90}
                    position="insideLeft"
                    style={{ textAnchor: "middle" }}
                  />
                </YAxis>
                <Tooltip formatter={(value) => formatAmount(value)} />
                <Legend />
                <Bar dataKey="totalVfcs" fill="#8884d8" name="VFC's" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p>Loading Chart...</p>
          )}
        </div>

        <div className="col-md-6 stretch-card grid-margin">
          <div className="d-flex justify-content-center mb-2">
            <strong>Member's in Circles</strong>
          </div>

          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis
                  dataKey="circleName"
                  angle={-90}
                  tick={{ fontSize: 14 }}
                  textAnchor="end"
                  interval={0}
                  height={80}
                />
                <YAxis
                  tickFormatter={(value) => value.toLocaleString()}
                  domain={[0, "auto"]}
                >
                  <Label
                    value="Member's in Numbers"
                    angle={-90}
                    position="left"
                    style={{ textAnchor: "middle" }}
                  />
                </YAxis>
                <Tooltip formatter={(value) => value.toLocaleString()} />
                <Legend />

                <Bar dataKey="totalMembers" fill="#52aa54" name="Members" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p>Loading Chart...</p>
          )}
        </div>
      </div>

      <div className="row">
        <div className="d-flex justify-content-center mb-2">
          <strong>Loans & Repayments in Circles</strong>
        </div>

        <div className="col-lg-12 grid-margin stretch-card">
          <div className="col-md-12 stretch-card grid-margin">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={500}>
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 35, bottom: 5 }}
                >
                  <XAxis
                    dataKey="circleName"
                    angle={-90}
                    tick={{ fontSize: 14 }}
                    textAnchor="end"
                    interval={0}
                    height={100}
                  />
                  <YAxis
                    tickFormatter={(value) => formatAmount(value)}
                    domain={[0, "auto"]}
                  >
                    <Label
                      value="Amount in Numbers"
                      angle={-90}
                      position="left"
                      style={{ textAnchor: "middle" }}
                    />
                  </YAxis>
                  <Tooltip formatter={(value) => formatAmount(value)} />
                  <Legend />

                  <Bar
                    dataKey="totalLoanAmount"
                    fill="#64b3f4"
                    name="Loan Amount"
                    onClick={(data) => handleBarClick(data)}
                  />
                  <Bar
                    dataKey="totalRepaymentAmount"
                    fill="#7bcf47"
                    name="Recovered Amount"
                    onClick={(data) => handleBarClick(data)}
                  />
                  <Bar
                    dataKey="balanceAmount"
                    fill="#e73827"
                    name="Balance Amount"
                    onClick={(data) => handleBarClick(data)}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p>Loading Chart...</p>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Circle Name</th>
                      <th>Total VFCs</th>
                      <th>Total Members</th>
                      <th>Total Loan Amount</th>
                      <th>Total Recovered Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.length > 0 ? (
                      chartData.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{item.circleName}</td>
                          <td>{item.totalVfcs}</td>
                          <td>{formatAmount(item.totalMembers)}</td>
                          <td>{formatAmount(item.totalLoanAmount)}</td>
                          <td>{formatAmount(item.totalRepaymentAmount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ClosingBalance open={openPopup} onClose={() => setOpenPopup(false)} />
      {error && <p className="text-danger">{error}</p>}

      {/*Circle Popup */}
      <CircleModal
        show={circleModalOpen}
        onClose={() => setCircleModalOpen(false)}
        selectedCircle={selectedCircle}
        divisionData={divisionData}
        onDivisionClick={handleDivisionClick}
        formatAmount={formatAmount}
      />
      {/*Division Popup */}
      <DivisionModal
        show={divisionModalOpen}
        onClose={() => setDivisionModalOpen(false)}
        selectedDivision={selectedDivision}
        vfcData={vfcData}
        formatAmount={formatAmount}
      />
    </div>
  );
};

export default Dashboard;
